import * as React from "react";
import {
  Button,
  Dialog, DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  TextField,
  Theme,
  WithStyles
} from "@material-ui/core";
import ICommunityMembership from "../types/ICommunityMembership";
import { Link, NavLink, RouteComponentProps } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography/Typography";
import BreadCrumb from "../../shared/components/BreadCrumb";
import Paper from "@material-ui/core/Paper";
import DateHelper from "../../shared/helpers/DateHelper";
import { connect } from "react-redux";
import { IPagedCommunityMgmtMemberships } from "../types/IPagedCommunityMgmtMemberships";
import ICommunityOwnership from "../types/ICommunityOwnership";
import { MembershipStatus } from "../types/MembershipStatus";
import { updateMembershipStatus } from "../api/communitiesApi";
import Loading from "../../shared/components/loading/Loading";
import HistoryIcon from "@material-ui/icons/History";
import SnackbarService from "../../core/services/SnackbarService";

const styles = (theme: Theme) => ({
  membershipTitle: {
    marginTop: theme.spacing.unit * 2,
  },
  membershipPaper: {
    padding: theme.spacing.unit * 3,
    marginBottom: theme.spacing.unit * 4
  },
  organizationPaper: {
    padding: theme.spacing.unit * 3,
    marginBottom: theme.spacing.unit * 2
  },
  dialogContentText: {
    marginBottom: theme.spacing.unit * 2
  }
});

interface IState {
  membership: ICommunityMembership | null,
  isLoading: boolean,
  disableDialogOpen: boolean
}

interface IProps extends WithStyles<typeof styles>, RouteComponentProps<any> {
  communityMemberships: IPagedCommunityMgmtMemberships,
  communityOwnerships: ICommunityOwnership[]
}

class CommunityManagementMembershipDetails extends React.Component<IProps, IState> {
  constructor(props: any) {
    super(props);

    const membership = this.props.communityMemberships.items
      .find((x: ICommunityMembership) => x.entity.nxtEntityId === this.props.match.params.nxtEntityId)

    if (!membership) {
      this.redirectToMembershipsOverview();
    }
    this.state = {
      isLoading: false,
      membership: membership || null,
      disableDialogOpen: false
    }
  }

  public async updateMembership(membershipId: number, newStatus: MembershipStatus) {
    this.setState({
      isLoading: true
    })
    await updateMembershipStatus(this.props.match.params.communityCode, membershipId, newStatus);
    this.setState({
      isLoading: false
    })
    this.redirectToMembershipsOverview();
    SnackbarService.Instance.showSnackbar('success', `Successfully updated membership to status ${newStatus}.`)
  }

  public redirectToMembershipsOverview() {
    this.props.history.push({
      pathname: `/community-management/${this.props.match.params.communityCode}/memberships`
    })
  }

  public renderTitleAndBreadcrumbs() {
    const { membership } = this.state;
    return (
      <>
        <Typography variant="title">{membership && `${membership.communityName} | `}Membership details</Typography>
        <Typography color="textSecondary" gutterBottom>
          <BreadCrumb value="Console" to="/"/>
          <BreadCrumb value="Community management" to="/community-management"/>
          <BreadCrumb value="Memberships overview"
                      to={`/community-management/${this.props.match.params.communityCode}/memberships`}/>
          {this.props.match.params.nxtEntityId}
        </Typography>
      </>
    )
  }

  public handleOpenDisableDialog = () => {
    this.setState({
      disableDialogOpen: true
    });
  };

  public handleCloseDisableDialog = () => {
    this.setState({
      disableDialogOpen: false
    })
  };

  public render() {
    const { membership } = this.state;
    const { classes } = this.props;

    if (!this.state || !membership) {
      return (
        <>
          {this.renderTitleAndBreadcrumbs()}
          <Typography>
            No membership was found or you do not have the rights to view this membership.
          </Typography></>
      )
    }

    if (this.state.isLoading) {
      return (
        <>
          {this.renderTitleAndBreadcrumbs()}
          <Loading/>
        </>
      )
    }
    return (
      <>
        {this.renderTitleAndBreadcrumbs()}

        <Typography className={classes.membershipTitle} variant="subheading" gutterBottom>
          Membership
        </Typography>

        <Paper className={classes.membershipPaper}>
          <Grid container spacing={24}>
            <Grid item>
              <TextField
                name="Request date"
                label="Request Date"
                value={DateHelper.FormatDate(membership.createdOn)}
                disabled
              />
            </Grid>
            <Grid item>
              <TextField
                name="status"
                label="Status"
                value={membership.status}
                disabled
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                name="communityName"
                label="Community"
                value={membership.communityName}
                disabled
              />
            </Grid>

            <Grid item xs={1} style={{ "marginLeft": 'auto'}}>
              <Button component={(componentProps: any) => <NavLink {...componentProps}
                                                                   to={{
                                                                     pathname: `/community-management/${this.props.match.params.communityCode}/memberships/${this.props.match.params.nxtEntityId}/history`,
                                                                     state: {
                                                                       membership: this.state.membership
                                                                     }
                                                                   }}
              />}>
                <Grid container direction="column" alignItems="center">
                  <Grid item>
                    <HistoryIcon color="secondary"/>
                  </Grid>
                  <Grid item>
                    <Typography color="secondary">
                      History
                    </Typography>
                  </Grid>
                </Grid>
              </Button>

            </Grid>
          </Grid>
        </Paper>

        <Typography variant="subheading" gutterBottom>
          Organization
        </Typography>

        <Paper className={classes.organizationPaper}>
          <Grid container spacing={24}>
            <Grid item>
              <TextField
                name="name"
                label="Name"
                value={membership.entity.name || ' '}
                margin="normal"
                disabled
              />
            </Grid>
            <Grid item>
              <TextField
                name="eori"
                label="EORI"
                value={membership.entity.eori || ' '}
                margin="normal"
                disabled
              />
            </Grid>
            <Grid item>
              <TextField
                name="duns"
                label="DUNS"
                value={membership.entity.duns || ' '}
                margin="normal"
                disabled
              />
            </Grid>
            <Grid item>
              <TextField
                name="vat"
                label="VAT"
                value={membership.entity.vat || ' '}
                margin="normal"
                disabled
              />
            </Grid>
          </Grid>
          <Grid container spacing={24} className="padding-bottom-24">
            <Grid item>
              <TextField
                name="nxtPortId"
                disabled
                label="NxtPort ID"
                value={membership.entity.nxtEntityId || ' '}
                margin="normal"
              />
            </Grid>
            <Grid item>
              <TextField
                name="createdOn"
                disabled
                label="Created On"
                value={membership.entity.createdOn ? DateHelper.FormatDate(membership.entity.createdOn) : ' '}
                margin="normal"
              />
            </Grid>
          </Grid>
        </Paper>

        {<Dialog
          open={this.state.disableDialogOpen}
          onClose={this.handleCloseDisableDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth={'md'}
        >
          <DialogTitle id="alert-dialog-title">
            Disable community membership
          </DialogTitle>
          <DialogContent>
            <DialogContentText className={classes.dialogContentText}>
              You are about to disable the membership of {membership.entity.name} for
              the {membership.communityName} community.
            </DialogContentText>

            <DialogContentText className={classes.dialogContentText}>
              The entity will no longer be able to subscribe to assets that are linked to this community and will no
              longer have access to community specific content (if applicable).
            </DialogContentText>
            <DialogContentText className={classes.dialogContentText}>
              Please be advised that the entity will still be able to use any assets linked to your community for which
              they were already approved. <Link to={'https://nxtport.atlassian.net/servicedesk/customer/portal/1'}>Contact
              our support team</Link> in case you want to revoke their access to any of
              these assets.
            </DialogContentText>

          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCloseDisableDialog} color="secondary"
            >
              Close
            </Button>
            <Button
              onClick={() => this.updateMembership(membership.id, MembershipStatus.DISABLED)}
              color="secondary"
              autoFocus
            >
              Disable membership
            </Button>
          </DialogActions>
        </Dialog>}

        {membership.status === MembershipStatus.ACTIVE && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={this.handleOpenDisableDialog}
          >
            Disable
          </Button>
        )}

        {membership.status === MembershipStatus.PENDING && (
          <Grid container spacing={24}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={() => this.updateMembership(membership.id, MembershipStatus.ACTIVE)}
              >
                Approve
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => this.updateMembership(membership.id, MembershipStatus.DENIED)}
              >
                Deny
              </Button>
            </Grid>
          </Grid>
        )}

        {(membership.status === MembershipStatus.DISABLED || membership.status === MembershipStatus.DENIED) && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => this.updateMembership(membership.id, MembershipStatus.ACTIVE)}
          >
            Activate
          </Button>
        )}
      </>
    )
  }
}

function mapStateToProps(state: any) {
  return {
    communityMemberships: state.communitiesReducer.communityMemberships ? state.communitiesReducer.communityMemberships : []
  };
}

export default connect(mapStateToProps)(withStyles(styles)(CommunityManagementMembershipDetails))




// WEBPACK FOOTER //
// ./src/modules/communities/components/CommunityManagementMembershipDetails.tsx